import {
  Button,
  ButtonProps,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState, useEffect, useContext } from "react";
import PageTitle from "../PageTitle";
import AddIcon from "@mui/icons-material/Add";
import { useOutletContext } from "react-router-dom";
import moment from "moment";
import { getShortenAddress } from "../../scripts/constants";
import toast from "react-hot-toast";
import LoadingTableRows from "../LoadingTableRows";
import { AppContext } from "../../scripts/context/App";

const CustomButton = styled(Button)<ButtonProps>(() => ({
  color: "#10269C",
  backgroundColor: "#00FFFF",
  boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
  borderRadius: "8px",
  minWidth: "150px",
  textTransform: "unset",
  "&:hover": {
    color: "#fff",
    backgroundColor: "#10269C",
  },
}));

export default function VestingManagement() {
  const { user } = useContext(AppContext);
  const {
    user: { address },
    contracts: {
      vesting: { contract },
      bullit: {
        data: { decimals },
      },
    },
  }: any = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [claimingVesting, setClaimingVesting] = useState(false);
  const [tableData, setTableDatas] = useState({
    headCells: [
      {
        id: "category_name",
        numeric: false,
        disablePadding: false,
        label: "Category Name",
      },
      {
        id: "category_address",
        numeric: false,
        disablePadding: false,
        label: "Category Address",
      },
      {
        id: "balance",
        numeric: false,
        disablePadding: false,
        label: "Balance",
      },
      {
        id: "vested",
        numeric: false,
        disablePadding: false,
        label: "Vested",
      },
      {
        id: "available",
        numeric: false,
        disablePadding: false,
        label: "Available",
      },
      {
        id: "vesting_count",
        numeric: false,
        disablePadding: false,
        label: "Vesting Count",
      },
      {
        id: "next_vesting_date",
        numeric: false,
        disablePadding: false,
        label: "Next Vesting Date",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });

  const getVestingData = async () => {
    // need to check out again
    if (!contract) return;
    const data = await contract.getCategories();

    setTableData({
      tableItems: data.map((item: any) => ({
        category_name: item.categoryName,
        category_address: getShortenAddress(item.categoryAddress),
        balance: `${(item.balance.toString() / 10 ** decimals).toFixed(4)} BULT`,
        vested: `${(item.collectedAllocation.toString() / 10 ** decimals).toFixed(4)} BULT`,
        available: `${(item.availableAllocation.toString() / 10 ** decimals).toFixed(4)
          } BULT`,
        vesting_count: item.vestingCount.toNumber(),
        next_vesting_date: moment(
          item.nextVestingDate.toNumber() * 1000
        ).format("dddd, MMMM Do YYYY, h:mm:ss A"),
      })),
    });

    setLoading(false);
  };

  const claimAvailableAllocation = async () => {
    if (!user.permissions.includes("claim_vesting"))
      return toast.error("You don't have permission to claim vesting");
    if (!contract) return;

    setClaimingVesting(true);
    try {
      const data = await contract.claimAvailableAllocation();

      await toast.promise(
        data.wait(),
        {
          loading: "Claiming vesting...",
          success: "Successfully claimed available allocation",
          error: "Failed to claim vesting",
        },
      );

      setClaimingVesting(false);
    } catch (error: any) {
      console.log(error, "error while claiming available allocation");

      if (error.code === 4001) return toast.error("Transaction rejected");
      toast.error(
        "Something went wrong. Please try again later or contact the administrator."
      );
    }

    getVestingData();
  };

  useEffect(() => {
    if (!user.permissions.includes("view_vesting")) return;

    getVestingData();
  }, [contract]);

  if (!user.permissions.includes("view_vesting"))
    return (
      <Box>
        <h1>You dont have permissions to view this page</h1>
      </Box>
    );

  if (
    !address ||
    address.toLowerCase() !== process.env.REACT_APP_ADMIN_ADDRESS?.toLowerCase()
  )
    return (
      <Box sx={{ height: "80vh" }}>
        <PageTitle
          title="Vesting Categories Management"
          description="Manage your Vesting Categories"
        />
        <Box sx={{ textAlign: "center" }}>
          <h3>Please connect to metamask with admin address</h3>
        </Box>
      </Box>
    );

  return (
    <>
      <PageTitle
        title="Vesting Categories Management"
        description="Manage your Vesting Categories"
      />
      <Box sx={{ p: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={
            claimingVesting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          onClick={claimAvailableAllocation}
          disabled={claimingVesting}
        >
          Claim Vesting Amount
        </Button>
      </Box>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ width: "100%", border: "1px solid rgba(224, 224, 224, 1)" }}>
          <TableContainer>
            <Table
              sx={{ minWidth: 1400 }}
              aria-labelledby="tableTitle"
              size="medium"
            >
              <TableHead>
                <TableRow>
                  {tableData.headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? "right" : "left"}
                      padding={headCell.disablePadding ? "none" : "normal"}
                    >
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <LoadingTableRows
                    elementsCount={tableData.headCells.length}
                  />
                ) : (
                  tableData.tableItems.map((row: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{row.category_name}</TableCell>
                      <TableCell>{row.category_address}</TableCell>
                      <TableCell>{row.balance}</TableCell>
                      <TableCell>{row.vested}</TableCell>
                      <TableCell>{row.available}</TableCell>
                      <TableCell>{row.vesting_count}</TableCell>
                      <TableCell>{row.next_vesting_date}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
